import React, { useCallback, useEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut, Maximize2, RotateCw, Loader2, ChevronLeft, ChevronRight, Search } from "lucide-react";
import * as pdfjs from "pdfjs-dist/legacy/build/pdf.mjs";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const btn = "inline-flex items-center justify-center h-7 min-w-7 px-1.5 rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 disabled:opacity-40 text-xs";

export function DocumentViewer({ blobUrl, contentType, fileName, onSwipe, fitMode = "page" }) {
  const isImage = (contentType || "").startsWith("image/") || (!(contentType || "").includes("pdf") && /\.(png|jpe?g|gif|webp|heic|heif|bmp)$/i.test(fileName || ""));
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [doc, setDoc] = useState(null);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1); // relative to "fit"
  const [rotation, setRotation] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imgNatural, setImgNatural] = useState(null);

  // Hover-Lupe (gleiche Einstellung wie im Durchblättern)
  const [lensOn, setLensOn] = useState(() => localStorage.getItem("inbox.lupe") !== "off");
  const [lensZoom, setLensZoom] = useState(() => Number(localStorage.getItem("inbox.lupe.zoom")) || 2.5);
  const [lens, setLens] = useState(null);
  const lensCanvasRef = useRef(null);
  const hiCanvasRef = useRef(null);
  const imgElRef = useRef(null);
  useEffect(() => { localStorage.setItem("inbox.lupe", lensOn ? "on" : "off"); }, [lensOn]);
  useEffect(() => { localStorage.setItem("inbox.lupe.zoom", String(lensZoom)); }, [lensZoom]);

  useEffect(() => {
    setDoc(null); setPage(1); setZoom(1); setRotation(0); setError(null); setImgNatural(null);
    if (!blobUrl || isImage) return;
    let cancelled = false;
    setLoading(true);
    pdfjs.getDocument({ url: blobUrl }).promise
      .then((d) => { if (!cancelled) setDoc(d); })
      .catch((e) => { if (!cancelled) setError(e?.message || "PDF konnte nicht gelesen werden"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [blobUrl, isImage]);

  const taskRef = useRef(null);
  const render = useCallback(async () => {
    if (!doc || !canvasRef.current || !containerRef.current) return;
    if (taskRef.current) { try { taskRef.current.cancel(); } catch { /* noop */ } taskRef.current = null; }
    const pg = await doc.getPage(page);
    if (!canvasRef.current || !containerRef.current) return;
    const box = containerRef.current.getBoundingClientRect();
    const base = pg.getViewport({ scale: 1, rotation: (pg.rotate + rotation) % 360 });
    const fitPage = Math.min((box.width - 24) / base.width, (box.height - 24) / base.height); const fit = fitMode === "width" ? (box.width - 24) / base.width : fitMode === "receipt" ? Math.min((box.width - 24) / base.width, fitPage * 2.2) : fitPage;
    const scale = Math.max(0.1, fit * zoom);
    const vp = pg.getViewport({ scale, rotation: (pg.rotate + rotation) % 360 });
    const dpr = window.devicePixelRatio || 1;
    const canvas = canvasRef.current;
    canvas.width = Math.floor(vp.width * dpr);
    canvas.height = Math.floor(vp.height * dpr);
    canvas.style.width = `${Math.floor(vp.width)}px`;
    canvas.style.height = `${Math.floor(vp.height)}px`;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const task = pg.render({ canvasContext: ctx, viewport: vp });
    taskRef.current = task;
    try { await task.promise; } catch (e) { if (e?.name !== "RenderingCancelledException") throw e; }
    if (taskRef.current === task) taskRef.current = null;
  }, [doc, page, zoom, rotation, fitMode]);

  useEffect(() => { render().catch(() => {}); }, [render]);

  // Hochauflösende Kopie der Seite für die Lupe (3× der Ansicht, max. 4096px)
  useEffect(() => {
    if (!doc || !lensOn || !containerRef.current) { hiCanvasRef.current = null; return undefined; }
    let cancelled = false;
    (async () => {
      const pg = await doc.getPage(page);
      if (cancelled || !containerRef.current) return;
      const box = containerRef.current.getBoundingClientRect();
      const base = pg.getViewport({ scale: 1, rotation: (pg.rotate + rotation) % 360 });
      const fitPage = Math.min((box.width - 24) / base.width, (box.height - 24) / base.height); const fit = fitMode === "width" ? (box.width - 24) / base.width : fitMode === "receipt" ? Math.min((box.width - 24) / base.width, fitPage * 2.2) : fitPage;
      const hiScale = Math.min(Math.max(0.1, fit * zoom) * 3, 4096 / Math.max(base.width, base.height));
      const vp = pg.getViewport({ scale: hiScale, rotation: (pg.rotate + rotation) % 360 });
      const c = document.createElement("canvas");
      c.width = Math.floor(vp.width); c.height = Math.floor(vp.height);
      try { await pg.render({ canvasContext: c.getContext("2d"), viewport: vp }).promise; } catch { return; }
      if (!cancelled) hiCanvasRef.current = c;
    })();
    return () => { cancelled = true; };
  }, [doc, page, zoom, rotation, lensOn, fitMode]);

  const LENS = 300;
  const onLensMove = (e) => {
    if (!lensOn) return;
    const src = isImage ? imgElRef.current : canvasRef.current;
    if (!src) return;
    const r = src.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width, py = (e.clientY - r.top) / r.height;
    if (px < 0 || px > 1 || py < 0 || py > 1 || (!isImage && loading)) { setLens(null); return; }
    const source = isImage ? src : (hiCanvasRef.current || src);
    const sw = isImage ? src.naturalWidth : source.width, sh = isImage ? src.naturalHeight : source.height;
    if (!sw || !sh) return;
    const regionW = (LENS / lensZoom) * (sw / r.width), regionH = (LENS / lensZoom) * (sh / r.height);
    const out = lensCanvasRef.current;
    if (out) {
      const ctx = out.getContext("2d");
      ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, LENS, LENS);
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(source, px * sw - regionW / 2, py * sh - regionH / 2, regionW, regionH, 0, 0, LENS, LENS);
    }
    const vw = window.innerWidth, vh = window.innerHeight;
    let left = e.clientX + 28;
    if (left + LENS > vw - 8) left = e.clientX - LENS - 28;
    setLens({ left, top: Math.min(Math.max(8, e.clientY - LENS / 2), vh - LENS - 8) });
  };
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (!lensOn || el.scrollHeight > el.clientHeight + 2 || el.scrollWidth > el.clientWidth + 2) return;
      e.preventDefault();
      setLensZoom((z) => Math.min(5, Math.max(1.5, +(z + (e.deltaY < 0 ? 0.5 : -0.5)).toFixed(1))));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [lensOn]);
  useEffect(() => {
    let t = null;
    const ro = new ResizeObserver(() => { clearTimeout(t); t = setTimeout(() => render().catch(() => {}), 120); });
    if (containerRef.current) ro.observe(containerRef.current);
    return () => { clearTimeout(t); ro.disconnect(); };
  }, [render]);

  const numPages = doc?.numPages || 1;

  // Touch: Pinch-Zoom (zwei Finger), Wischen (Seite/Beleg wechseln), Doppeltipp (Einpassen ↔ 200 %)
  const gestureRef = useRef({ dist: 0, scale: 1, startX: 0, startY: 0, moved: false, lastTap: 0 });
  const [liveScale, setLiveScale] = useState(1);
  const pageRef = useRef(page); pageRef.current = page;
  const numPagesRef = useRef(numPages); numPagesRef.current = numPages;
  const zoomRef = useRef(zoom); zoomRef.current = zoom;
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const g = gestureRef.current;
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const onStart = (e) => {
      if (e.touches.length === 2) { g.dist = dist(e.touches); g.scale = 1; e.preventDefault(); }
      else if (e.touches.length === 1) { g.startX = e.touches[0].clientX; g.startY = e.touches[0].clientY; g.moved = false; }
    };
    const onMove = (e) => {
      if (e.touches.length === 2 && g.dist) {
        e.preventDefault();
        g.scale = Math.min(5 / zoomRef.current, Math.max(0.25 / zoomRef.current, dist(e.touches) / g.dist));
        setLiveScale(g.scale);
      } else if (e.touches.length === 1) {
        if (Math.abs(e.touches[0].clientX - g.startX) > 8 || Math.abs(e.touches[0].clientY - g.startY) > 8) g.moved = true;
      }
    };
    const onEnd = (e) => {
      if (g.dist) {
        if (e.touches.length < 2) { setZoom((z) => +(Math.min(5, Math.max(0.25, z * g.scale))).toFixed(2)); setLiveScale(1); g.dist = 0; }
        return;
      }
      if (e.changedTouches.length !== 1) return;
      const dx = e.changedTouches[0].clientX - g.startX, dy = e.changedTouches[0].clientY - g.startY;
      const now = Date.now();
      if (!g.moved) {
        if (now - g.lastTap < 300) { setZoom((z) => (z === 1 ? 2 : 1)); g.lastTap = 0; } else g.lastTap = now;
        return;
      }
      const canScrollX = el.scrollWidth > el.clientWidth + 2;
      if (!canScrollX && Math.abs(dx) > 60 && Math.abs(dy) < 50) {
        const dir = dx < 0 ? 1 : -1;
        const next = pageRef.current + dir;
        if (next >= 1 && next <= numPagesRef.current) setPage(next);
        else onSwipe?.(dir);
      }
    };
    el.addEventListener("touchstart", onStart, { passive: false });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    return () => { el.removeEventListener("touchstart", onStart); el.removeEventListener("touchmove", onMove); el.removeEventListener("touchend", onEnd); };
  }, [onSwipe]);
  // Tastatur: + / − zoomen, 0 einpassen, R drehen, ←/→ Beleg bzw. Seite wechseln, Bild↑/↓ Seite
  useEffect(() => {
    if (!blobUrl) return undefined;
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tag) || e.target?.isContentEditable || e.metaKey || e.ctrlKey || e.altKey) return;
      const zoomIn = () => setZoom((z) => Math.min(5, +(z + 0.25).toFixed(2)));
      const zoomOut = () => setZoom((z) => Math.max(0.25, +(z - 0.25).toFixed(2)));
      const flip = (dir) => {
        const next = pageRef.current + dir;
        if (onSwipe && (numPagesRef.current <= 1 || next < 1 || next > numPagesRef.current)) onSwipe(dir);
        else if (next >= 1 && next <= numPagesRef.current) setPage(next);
      };
      const map = { "+": zoomIn, "=": zoomIn, "-": zoomOut, "_": zoomOut, "0": () => setZoom(1), "r": () => setRotation((r) => (r + 90) % 360), "R": () => setRotation((r) => (r + 90) % 360),
        ArrowRight: () => flip(1), ArrowLeft: () => flip(-1), PageDown: () => flip(1), PageUp: () => flip(-1) };
      const fn = map[e.key];
      if (fn) { e.preventDefault(); fn(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [blobUrl, onSwipe]);

  const liveStyle = liveScale !== 1 ? { transform: `scale(${liveScale})`, transformOrigin: "center center" } : undefined;

  const imgStyle = imgNatural && zoom !== 1 ? { width: `${imgNatural.w * zoom}px`, maxWidth: "none", maxHeight: "none" } : {};

  return (
    <div className="flex flex-col h-full w-full" data-testid="document-viewer">
      <div className="flex items-center gap-1 px-2 py-1 border-b border-slate-200 bg-slate-50 text-xs text-slate-600">
        <button type="button" className={btn} onClick={() => setZoom((z) => Math.max(0.25, +(z - 0.25).toFixed(2)))} title="Verkleinern" data-testid="viewer-zoom-out"><ZoomOut className="h-3.5 w-3.5" /></button>
        <span className="w-12 text-center tabular-nums" data-testid="viewer-zoom-level">{Math.round(zoom * 100)}%</span>
        <button type="button" className={btn} onClick={() => setZoom((z) => Math.min(5, +(z + 0.25).toFixed(2)))} title="Vergrössern" data-testid="viewer-zoom-in"><ZoomIn className="h-3.5 w-3.5" /></button>
        <button type="button" className={btn} onClick={() => setZoom(1)} title="Seite einpassen" data-testid="viewer-fit"><Maximize2 className="h-3.5 w-3.5 mr-1" />Einpassen</button>
        {!isImage && <button type="button" className={btn} onClick={() => setRotation((r) => (r + 90) % 360)} title="Drehen" data-testid="viewer-rotate"><RotateCw className="h-3.5 w-3.5" /></button>}
        <button type="button" onClick={() => { setLensOn((v) => !v); setLens(null); }}
          className={`${btn} whitespace-nowrap ${lensOn ? "!bg-emerald-600 !text-white !border-emerald-600" : ""}`}
          title={lensOn ? "Lupe ausschalten" : "Lupe einschalten – Maus auf das Dokument zeigt den Ausschnitt vergrössert (Mausrad = Zoomstufe)"} data-testid="viewer-lens-toggle">
          <Search className="h-3.5 w-3.5 mr-1" />Lupe {lensOn ? `${lensZoom}×` : "aus"}
        </button>
        {!isImage && numPages > 1 && (
          <div className="ml-auto flex items-center gap-1">
            <button type="button" className={btn} disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="viewer-prev-page"><ChevronLeft className="h-3.5 w-3.5" /></button>
            <span className="tabular-nums" data-testid="viewer-page-indicator">Seite {page} / {numPages}</span>
            <button type="button" className={btn} disabled={page >= numPages} onClick={() => setPage((p) => p + 1)} data-testid="viewer-next-page"><ChevronRight className="h-3.5 w-3.5" /></button>
          </div>
        )}
        {(isImage || numPages <= 1) && <span className="ml-auto truncate max-w-[40%]" title={fileName}>{fileName}</span>}
        <span className="hidden lg:inline text-[10px] text-slate-400 ml-2 whitespace-nowrap" title="Tastatur: + / − zoomen · 0 einpassen · R drehen · ← → Beleg/Seite wechseln" data-testid="viewer-key-hint">⌨ + − 0 R ← →</span>
      </div>
      <div ref={containerRef} className="flex-1 min-h-0 overflow-auto bg-slate-100 flex touch-pan-x touch-pan-y select-none" style={{ WebkitUserSelect: "none" }} data-testid="viewer-canvas-area"
        onMouseMove={onLensMove} onMouseLeave={() => setLens(null)}>
        {isImage ? (
          <img ref={imgElRef} src={blobUrl} alt={fileName} onLoad={(e) => setImgNatural({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
            className={`${zoom === 1 ? (fitMode === "width" ? "m-auto w-full h-auto" : fitMode === "receipt" ? "m-auto max-w-full h-auto" : "m-auto max-w-full max-h-full object-contain") : "m-auto"} ${lensOn ? "cursor-zoom-in" : ""}`} style={{ ...(zoom === 1 && fitMode === "receipt" ? { maxHeight: "220%" } : {}), ...imgStyle, ...liveStyle }} draggable={false} data-testid="inbox-preview-image" />
        ) : error ? (
          <div className="m-auto text-sm text-slate-600 text-center p-6">
            {error}<br />
            <a href={blobUrl} target="_blank" rel="noopener noreferrer" className="underline text-indigo-700">In neuem Tab öffnen</a>
          </div>
        ) : (
          <>
            {loading && <div className="m-auto text-slate-500 text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Lädt Vorschau …</div>}
            <canvas ref={canvasRef} className={`m-auto shadow-md bg-white ${loading ? "hidden" : ""} ${lensOn ? "cursor-zoom-in" : ""}`} style={liveStyle} data-testid="inbox-preview-object" />
          </>
        )}
      </div>
      {lensOn && (
        <canvas ref={lensCanvasRef} width={LENS} height={LENS}
          className={`pointer-events-none fixed z-[200] rounded-2xl border-2 border-emerald-400 shadow-2xl bg-white ${lens ? "" : "hidden"}`}
          style={lens ? { left: lens.left, top: lens.top, width: LENS, height: LENS } : undefined} data-testid="viewer-lens" />
      )}
    </div>
  );
}
